import {ChangeDetectionStrategy, Component, ViewChild, Inject} from '@angular/core';
import {MdlDialogService} from '@angular-mdl/core';
import {EQueryDetailsStorage} from '../../../../e-queries/e-query-details/e-query-details-storage';
import {Settings, UrlBuilder} from '../../../../core';
import {map, publishReplay, refCount, take} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {routeParams} from '../../../../shared/route-helpers';
import {ActivatedRoute} from '@angular/router';
import {ExternalEQueryAccessionInfo} from '../../../../e-queries/accession-info/external-e-query-accession-info';
import {EQueryInfo} from '../../../../e-queries/models/e-query-info';
import {EQueryAttachment} from '../../../../e-queries/models/e-query-attachment';
import { ExternalUserReadEQueryEmitter, ReadEQueryEmitter } from '../../../../e-queries/read-e-query-emitter';
import {EQueryResponse} from '../../../../e-queries/models/e-query-response';
import {InvestigatorSitesStorage} from '../../../../sites';
import {GlobalPopupComponent} from '../../../../global-popup';
import { InvestigatorEQueriesStorage } from '../../investigator-e-queries-storage';
import { InvestigatorEQueriesCountersStorage } from '../../investigator-e-queries-counters-storage';
import {EQueryAttachmentsDownloader} from '../../../../e-queries/e-query-details/e-query-attachments-downloader';
import { ActivityLogger } from '../../../../activities/activity-logger';
import {EQuerySubmittedResponse} from '../../../../e-queries/models/e-query-submitted-response';
import {EQueryCCQuestion} from '../../../../admin/e-query/models/e-query-c-c-question';
import { ActivitySiteDetail } from '../../../../activities/activity-data';

@Component({
    selector: 'investigator-e-query-details-dialog',
    template: require('./investigator-e-query-details-dialog.component.html'),
    styles: [require('./investigator-e-query-details-dialog.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvestigatorEQueryDetailsDialogComponent {
    public accessionData$: Observable<ExternalEQueryAccessionInfo>;
    public eQueryData$: Observable<EQueryInfo>;
    public attachments$: Observable<EQueryAttachment[]>;
    public comments$: Observable<string>;
    public screenResultUrl$: Observable<any[]>;
    public submittedResponses$: Observable<EQuerySubmittedResponse[]>;
    public questions$: Observable<EQueryCCQuestion[]>;
    public shouldBeResponded$: Observable<boolean>;
    public status$: Observable<string>;
    public age$: Observable<string>;
    public isActiveSubmit$ = new BehaviorSubject<boolean>(false);

    @ViewChild(GlobalPopupComponent)
    private popup: GlobalPopupComponent;
    private eQueryId: number;

    constructor(
        private readonly dialogService: MdlDialogService,
        private readonly route: ActivatedRoute,
        private readonly storage: EQueryDetailsStorage,
        private readonly investigatorSiteStorage: InvestigatorSitesStorage,
        private readonly settings: Settings,
        @Inject(ReadEQueryEmitter) private readonly readEQueryEmitter: ExternalUserReadEQueryEmitter,
        private readonly urlBuilder: UrlBuilder,
        private readonly eQueriesStorage: InvestigatorEQueriesStorage,
        private readonly eQueriesCountersStorage: InvestigatorEQueriesCountersStorage,
        private readonly eQueryAttachmentsDownloader: EQueryAttachmentsDownloader,
        readonly activityLogger: ActivityLogger,
    ) {
        this.eQueryDetailsCreation();
        this.logEQueryActivity();
    }

    public async downloadAttachment(attachmentId: number): Promise<boolean> {
        return this.eQueryAttachmentsDownloader.downloadInvestigatorAttachment(attachmentId, this.eQueryId);
    }

    public async previewAttachment(attachmentId: number): Promise<boolean> {
        return this.eQueryAttachmentsDownloader.previewInvestigatorAttachment(attachmentId, this.eQueryId);
    }

    public async downloadResponseAttachment(attachmentId: number): Promise<boolean> {
        return this.eQueryAttachmentsDownloader.downloadResponseAttachmentWithTracking(attachmentId);
    }

    public async previewResponseAttachment(attachmentId: number): Promise<boolean> {
        return this.eQueryAttachmentsDownloader.previewResponseAttachment(attachmentId);
    }

    public async response(response: EQueryResponse) {
        try {
            this.isActiveSubmit$.next(true);
            await this.storage.createEQueryResponse(this.eQueryId, response).toPromise();
            const accessionData = await this.accessionData$.pipe(take(1)).toPromise();
            this.eQueriesCountersStorage.decreaseCounters(accessionData.siteId, [this.eQueryId]);
            this.eQueriesStorage.forceLoadEQueries();
            this.close();
        } finally {
            this.isActiveSubmit$.next(false);
        }
    }

    public close() {
        this.popup.onBackToPortal();
    }

    public onPopupClosing() {
        this.readEQuery();
    }

    private async logEQueryActivity() {
        const accession = await this.accessionData$.pipe(take(1)).toPromise();
        this.activityLogger.logAccessEQueryDetailView(new ActivitySiteDetail(accession.siteId));
    }

    private eQueryDetailsCreation(): void {
        const eQueryId$ = this.route.params.pipe(map(x => {
            const eQueryId = x[routeParams.eQueryId] as string | undefined;
            return this.eQueryId = parseInt(eQueryId, 10);
        }));
        const eQueryDetails$ = this.storage.getInvestigatorEQueryDetails(eQueryId$);

        this.eQueryData$ = eQueryDetails$.pipe(map(x => x.eQueryData));
        this.accessionData$ = eQueryDetails$.pipe(map(x => x.accessionData));
        this.attachments$ = eQueryDetails$.pipe(map(x => x.attachments));
        this.comments$ = eQueryDetails$.pipe(map(x => x.eQueryData.commentText));
        this.questions$ = eQueryDetails$.pipe(map(x => x.questions));
        this.shouldBeResponded$ = eQueryDetails$.pipe(map(x => x.eQueryData.shouldBeResponded));
        this.submittedResponses$ = eQueryDetails$.pipe(map(x => x.responseDetails));
        this.status$ = eQueryDetails$.pipe(map(x => x.eQueryData.statusName));
        this.age$ = eQueryDetails$.pipe(map(x => x.eQueryData.eQueryAge.formattedValue));

        this.screenResultUrl$ = combineLatest([this.investigatorSiteStorage.loadSites(), this.accessionData$]).pipe(
            map(([sites, accession]) => {
                const site = sites.find(item => item.siteId === accession.siteId);
                const url = this.urlBuilder
                    .esa()
                    .site(site)
                    .onScreenResults()
                    .standardTestResults()
                    .testResults()
                    .url();
                return url;
            }),
            publishReplay(1),
            refCount());
    }

    private async readEQuery() {
        const eQueryData = await this.eQueryData$.pipe(take(1)).toPromise();

        this.readEQueryEmitter.emit({
            eQueryId: eQueryData.eQueryId,
            afterRead: eq => {
                if (eq.eQueryStatus !== eQueryData.statusName) {
                    this.eQueriesStorage.forceLoadEQueries();
                }
                eq.eQueryStatus = eQueryData.statusName;    
            },
        });
    }
}
